import React, { useEffect, useState } from "react";
import { Plus } from "lucide-react";

import { Button } from "@/components/primitives/Button";
import { Dialog } from "@/components/primitives/Dialog";
import { Input } from "@/components/primitives/Input";
import { Select } from "@/components/primitives/Select";
import { Flow, FlowCreate, FlowType, SoftnuSourceConfig, StepCreate } from "@/types";

import { StepEditor } from "./StepEditor";

interface FlowFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (flow: FlowCreate) => Promise<boolean>;
  initialFlow?: Flow | null;
}

const emptyStep = (): StepCreate => ({
  sequence: 0,
  name: "",
  step_type: "http",
  method: "GET",
  url: "",
  headers: {},
  body: null,
  content_type: null,
  expected_status: 200,
  response_assertion: null,
  db_config: null,
  multipart_config: null,
  timeout_seconds: 30,
  period_minutes: 15,
  retry_intervals_minutes: [15, 10, 5],
  action_message: null,
  is_active: true,
  action_configs: [],
});

const emptySource = (): SoftnuSourceConfig => ({
  health_url: "",
  health_interval_minutes: 5,
  health_expected_status: 200,
  health_timeout_seconds: 30,
  health_headers: {},
  logs_url: null,
  logs_delete_url: null,
  delete_after_import: true,
  start_index: 0,
  logs_interval_minutes: 5,
  logs_timeout_seconds: 30,
  logs_headers: {},
  page_size: 100,
  max_pages_per_poll: 10,
});

export const FlowForm: React.FC<FlowFormProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initialFlow,
}) => {
  const [name, setName] = useState("");
  const [flowType, setFlowType] = useState<FlowType>("standard");
  const [source, setSource] = useState<SoftnuSourceConfig>(emptySource);
  const [healthHeaders, setHealthHeaders] = useState("{}");
  const [logsHeaders, setLogsHeaders] = useState("{}");
  const [steps, setSteps] = useState<StepCreate[]>([emptyStep()]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      if (initialFlow) {
        setName(initialFlow.name);
        setFlowType(initialFlow.flow_type ?? "standard");
        setSource(initialFlow.source_config ?? emptySource());
        setHealthHeaders(JSON.stringify(initialFlow.source_config?.health_headers ?? {}, null, 2));
        setLogsHeaders(JSON.stringify(initialFlow.source_config?.logs_headers ?? {}, null, 2));
        setSteps(
          initialFlow.steps.filter((step) => !step.is_managed).map((step) => ({
            id: step.id,
            sequence: step.sequence,
            name: step.name,
            step_type: step.step_type,
            method: step.method,
            url: step.url,
            headers: step.headers,
            body: step.body,
            content_type: step.content_type,
            expected_status: step.expected_status,
            response_assertion: step.response_assertion ?? null,
            db_config: step.db_config,
            multipart_config: step.multipart_config,
            timeout_seconds: step.timeout_seconds,
            period_minutes: step.period_minutes,
            retry_intervals_minutes: step.retry_intervals_minutes,
            action_message: step.action_message,
            is_active: step.is_active,
            action_configs: step.action_configs || [],
          })),
        );
      } else {
        setName("");
        setFlowType("standard");
        setSource(emptySource());
        setHealthHeaders("{}");
        setLogsHeaders("{}");
        setSteps([emptyStep()]);
      }
      setError(null);
    }
  }, [isOpen, initialFlow]);

  const handleAddStep = () => {
    setSteps((prev) => [...prev, { ...emptyStep(), sequence: prev.length }]);
  };

  const handleStepChange = (index: number, updated: StepCreate) => {
    setSteps((prev) => prev.map((step, i) => (i === index ? { ...updated, sequence: i } : step)));
  };

  const handleRemoveStep = (index: number) => {
    setSteps((prev) =>
      prev
        .filter((_, i) => i !== index)
        .map((step, i) => ({ ...step, sequence: i })),
    );
  };

  const validate = (): string | null => {
    if (!name.trim()) return "Flow name is required";
    if (flowType === "softnu_checks") {
      try {
        const health = new URL(source.health_url);
        if (!(["http:", "https:"].includes(health.protocol))) return "Health URL must use HTTP or HTTPS";
        if (source.logs_url) {
          const logs = new URL(source.logs_url);
          if (!(["http:", "https:"].includes(logs.protocol))) return "Logs URL must use HTTP or HTTPS";
          if (source.delete_after_import && !source.logs_delete_url) return "Enter the source cleanup URL or disable source cleanup";
          if (source.logs_delete_url) {
            const cleanup = new URL(source.logs_delete_url);
            if (cleanup.origin !== logs.origin) return "Logs and cleanup URLs must use the same origin";
          }
        }
      } catch {
        return "Enter a valid absolute health URL and, if used, logs URL";
      }
      for (const [label, value] of [["Health headers", healthHeaders], ["Logs headers", logsHeaders]] as const) {
        try {
          const parsed = JSON.parse(value);
          if (!parsed || Array.isArray(parsed) || typeof parsed !== "object" || Object.values(parsed).some((item) => typeof item !== "string")) {
            return `${label} must be a JSON object of string values`;
          }
        } catch {
          return `${label} must be valid JSON`;
        }
      }
      if (source.health_interval_minutes < 1 || source.logs_interval_minutes < 1) return "Poll intervals must be at least one minute";
      if (source.page_size < 1 || source.page_size > 1000) return "Page size must be between 1 and 1000";
      if (!Number.isSafeInteger(source.start_index) || source.start_index < 0) return "Starting index must be a nonnegative integer";
      return null;
    }
    if (steps.length === 0) return "At least one step is required";
    for (const step of steps) {
      if (!step.name.trim()) return "Each step requires a name";
      if (step.step_type === "http") {
        if (!step.url?.trim()) return "Each HTTP step requires a URL";
        if (step.expected_status == null || step.expected_status < 100 || step.expected_status > 599) {
          return "Each HTTP step requires a valid expected status code";
        }
      } else if (step.step_type === "database") {
        if (!step.db_config?.connection?.connection_id) {
          return "Each database step requires a connection profile";
        }
      }
      if (step.period_minutes < 1) return "Period must be at least 1 minute";
      if (step.response_assertion && !/^[^.\s]+(?:\.[^.\s]+)*$/.test(step.response_assertion.path)) {
        return "Enter a response field such as instance.state";
      }
      if (typeof step.headers !== "object" || step.headers === null) {
        return "Headers must be valid JSON";
      }
    }
    return null;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setIsLoading(true);
    const success = await onSubmit({
      name: name.trim(),
      is_active: initialFlow?.is_active ?? true,
      flow_type: flowType,
      source_config: flowType === "softnu_checks" ? {
        ...source,
        health_url: source.health_url.trim(),
        logs_url: source.logs_url?.trim() || null,
        logs_delete_url: source.logs_url ? source.logs_delete_url?.trim() || null : null,
        health_headers: JSON.parse(healthHeaders),
        logs_headers: JSON.parse(logsHeaders),
      } : null,
      steps: flowType === "standard" ? steps : [],
    });
    setIsLoading(false);
    if (success) {
      onClose();
    }
  };

  const footer = (
    <>
      <Button type="button" variant="ghost" onClick={onClose}>
        Cancel
      </Button>
      <Button type="submit" form="flow-form" isLoading={isLoading}>
        {initialFlow ? "Save changes" : "Create flow"}
      </Button>
    </>
  );

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={initialFlow ? "Edit flow" : "Create flow"}
      footer={footer}
      className="max-w-4xl"
    >
      <form id="flow-form" onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div
            className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300"
            role="alert"
          >
            {error}
          </div>
        )}

        <Input
          label="Flow name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. API Health Checks"
          required
        />

        <Select
          label="Flow type"
          value={flowType}
          disabled={Boolean(initialFlow)}
          onChange={(event) => {
            const nextType = event.target.value as FlowType;
            setFlowType(nextType);
            if (!name.trim() && nextType === "softnu_checks") setName("Softnu Checks");
            if (name === "Softnu Checks" && nextType === "standard") setName("");
          }}
          options={[
            { value: "standard", label: "Standard — add steps manually" },
            { value: "softnu_checks", label: "Softnu Checks — discover services from logs" },
          ]}
        />

        {flowType === "softnu_checks" ? (
          <div className="space-y-5">
            <p className="text-sm text-slate-600 dark:text-slate-300">
              The health URL is checked independently. Add a logs URL to discover service steps automatically.
            </p>
            <Input label="Health URL" type="url" required placeholder="https://example.com/health" value={source.health_url} onChange={(event) => setSource({ ...source, health_url: event.target.value })} />
            <div className="grid gap-4 sm:grid-cols-3">
              <Input label="Health interval (minutes)" type="number" min={1} value={source.health_interval_minutes} onChange={(event) => setSource({ ...source, health_interval_minutes: Number(event.target.value) })} />
              <Input label="Expected HTTP status" type="number" min={100} max={599} value={source.health_expected_status} onChange={(event) => setSource({ ...source, health_expected_status: Number(event.target.value) })} />
              <Input label="Health timeout (seconds)" type="number" min={1} value={source.health_timeout_seconds} onChange={(event) => setSource({ ...source, health_timeout_seconds: Number(event.target.value) })} />
            </div>
            <Input label="Health headers (JSON)" value={healthHeaders} onChange={(event) => setHealthHeaders(event.target.value)} />
            <Input label="Logs URL (GET, optional)" type="url" placeholder="https://example.com/backendapi/healthlog/getall" value={source.logs_url ?? ""} onChange={(event) => {
              const logsUrl = event.target.value;
              let cleanupUrl = source.logs_delete_url;
              try {
                const parsed = new URL(logsUrl);
                if (parsed.pathname.replace(/\/$/, "").endsWith("/healthlog/getall")) {
                  parsed.pathname = parsed.pathname.replace(/getall\/?$/, "removeRange");
                  parsed.search = "";
                  parsed.hash = "";
                  cleanupUrl = parsed.toString();
                }
              } catch { /* Keep the current cleanup URL while typing. */ }
              setSource({ ...source, logs_url: logsUrl || null, logs_delete_url: cleanupUrl });
            }} />
            {source.logs_url && (
              <>
                <p className="text-sm text-slate-600 dark:text-slate-300">The poller adds inclusive fromIndex/toIndex parameters, saves each batch locally, then optionally removes only the archived source ranges. Do not include range parameters in these URLs.</p>
                <label className="flex items-start gap-3 text-sm text-slate-700 dark:text-slate-200">
                  <input type="checkbox" className="mt-1" checked={source.delete_after_import} onChange={(event) => setSource({ ...source, delete_after_import: event.target.checked })} />
                  <span>Delete source logs after saving locally. This removes logs from the source backend; failed cleanup is retained for retry.</span>
                </label>
                {source.delete_after_import && <Input label="Source cleanup URL (DELETE)" type="url" required placeholder="https://example.com/backendapi/healthlog/removeRange" value={source.logs_delete_url ?? ""} onChange={(event) => setSource({ ...source, logs_delete_url: event.target.value || null })} />}
                <div className="grid gap-4 sm:grid-cols-3">
                  <Input label="Logs interval (minutes)" type="number" min={1} value={source.logs_interval_minutes} onChange={(event) => setSource({ ...source, logs_interval_minutes: Number(event.target.value) })} />
                  <Input label="Indexes per request" type="number" min={1} max={1000} value={source.page_size} onChange={(event) => setSource({ ...source, page_size: Number(event.target.value) })} />
                  <Input label="Maximum pages per poll" type="number" min={1} max={100} value={source.max_pages_per_poll} onChange={(event) => setSource({ ...source, max_pages_per_poll: Number(event.target.value) })} />
                  <Input label="Logs timeout (seconds)" type="number" min={1} value={source.logs_timeout_seconds} onChange={(event) => setSource({ ...source, logs_timeout_seconds: Number(event.target.value) })} />
                  <Input label="Starting index" type="number" min={0} value={source.start_index} onChange={(event) => setSource({ ...source, start_index: Number(event.target.value) })} />
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400">First request: fromIndex={source.start_index}, toIndex={source.start_index + source.page_size - 1}. Later requests resume from the saved next index. Changing the starting index restarts scanning; existing event IDs prevent duplicates.</p>
                <Input label="Logs headers (JSON)" value={logsHeaders} onChange={(event) => setLogsHeaders(event.target.value)} />
              </>
            )}
          </div>
        ) : (
          <>
            <div className="space-y-4">
              {steps.map((step, index) => (
                <StepEditor
                  key={index}
                  step={step}
                  index={index}
                  onChange={handleStepChange}
                  onRemove={handleRemoveStep}
                />
              ))}
            </div>

            <Button type="button" variant="secondary" onClick={handleAddStep} className="gap-1.5">
              <Plus className="h-4 w-4" />
              Add step
            </Button>
          </>
        )}
      </form>
    </Dialog>
  );
};
